import React, { useState, useEffect, useMemo } from 'react';
import { Button, TextField, Grid, styled, CircularProgress, Typography, Card, MenuItem, Menu, Checkbox, FormControlLabel, InputAdornment, IconButton } from '@mui/material';
import { Formik, Form, Field } from 'formik';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import * as Yup from 'yup';
import { debounce } from 'lodash';
import SnackbarComponent from '../../../utils/SnackbarComponent';
import { createAssignment, getAllUsersByIssuerId, getAllCredentialsByIssuer, createAssignmentToUnverifiedUser, getAllUsersInDB, getIssuerUserId, validateEmail } from '../../../services/apiservice';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../../contexts/authContext';
import { ExpandLess } from '@mui/icons-material';
import { useApiData } from '../../../hooks/useApi';


const RedAsterisk = styled('span')({
  color: 'red',
});

const CustomMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    maxHeight: 250,
    width: 'calc(37% - 38px)',
    marginTop: theme.spacing(1),
  },
}));

function AssignmentForm() {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [users, setUsers] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElCredential, setAnchorElCredential] = useState(null);
  const [isAddingNewUser, setIsAddingNewUser] = useState(false);
  const [userExistsError, setUserExistsError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [existingUserID, setExistingUserID] = useState(null);
  const [emailValidationError, setEmailValidationError] = useState(null);
  const { user } = useAuth();
  const issuerId = user.IssuerID;
  const { data: fetchedUsers } = useApiData(() => getAllUsersByIssuerId(issuerId).then((res) => res.data.filter((u) => u.UserIsVerified)), [issuerId]);
  const { data: fetchedCredentials } = useApiData(() => getAllCredentialsByIssuer(issuerId).then((res) => res.data), [issuerId]);

  useEffect(() => {
    if (fetchedUsers) {
      setUsers(fetchedUsers);
    }
  }, [fetchedUsers]);

  useEffect(() => {
    if (fetchedCredentials) {
      setCredentials(fetchedCredentials);
    }
  }, [fetchedCredentials]);

  const validationSchema = Yup.object({
    AssignmentUserID: Yup.string().required('User is required'),
    AssignmentCredentialID: Yup.array().min(1, 'At least one credential is required'),
  });

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleUserClick = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCredentialClick = (event) => {
    setAnchorElCredential(event.currentTarget);
  };

  const handleCloseUser = () => {
    setAnchorElUser(null);
  };

  const handleCloseCredential = () => {
    setAnchorElCredential(null);
  };

  const clearUserSelection = (setFieldValue) => {
    setFieldValue('AssignmentUserID', '');
  };

  const clearCredentialSelection = (setFieldValue) => {
    setFieldValue('AssignmentCredentialID', []);
  };

  const debouncedCheckUserExists = useMemo(
    () => debounce(async (email, setFieldValue) => {
      if (email) {
        try {
          const lowerCaseEmail = email.toLowerCase();
          // const validateResponse = await validateEmail(lowerCaseEmail);
          // if (!validateResponse) {
          //   setEmailValidationError('Invalid email address. Please provide a valid email.');
          // } else {
          setEmailValidationError(null);
          const response = await getAllUsersInDB();
          const users = response.data;
          const existingUser = users.find((user) => user.UserEmail === lowerCaseEmail);

          if (existingUser) {
            setUserExistsError('User already exists');
            setExistingUserID(existingUser.UserID);
          } else {
            setUserExistsError(null);
            setExistingUserID(null);
          }
          // }
        } catch (error) {
          console.error('Error checking user existence:', error);
        }
      }
    }, 300),
    [setEmailValidationError]
  );

  const filteredUsers = users.filter((user) =>
    user.UserID.toString().includes(searchQuery) ||
    user.UserEmail?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  return (
    <>
      <Helmet>
        <title>Assignment Form</title>
      </Helmet>
      <SnackbarComponent
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
        style={{ marginTop: '50px' }}
      />
      <Formik
        initialValues={{
          AssignmentUserID: '',
          AssignmentCredentialID: [],
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          try {
            let response;
            if (isAddingNewUser && values.AssignmentUserID.includes('@')) {
              const issuerUserId = await getIssuerUserId(issuerId);
              const payload = { userEmail: values.AssignmentUserID, credentialIds: values.AssignmentCredentialID, UserCreatorID: issuerUserId };
              response = await createAssignmentToUnverifiedUser(payload);
            }
            else {
              const userEmail = users.find(user => user.UserID === values.AssignmentUserID)?.UserEmail || '';
              const payload = { userEmail, credentialIds: values.AssignmentCredentialID };
              response = await createAssignment(payload);
            }
            if (response.success) {
              setSnackbarMessage('Assignment created successfully!');
              setSnackbarSeverity('success');
              setIsAddingNewUser(false);
              resetForm();
            } else {
              setSnackbarMessage('Failed to create assignment.');
              setSnackbarSeverity('error');
            }
            setSnackbarOpen(true);
          } catch (error) {
            console.error('Submission error:', error);
            const errorMessage = error.response?.data?.message || 'An unexpected error occurred.';
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ errors, touched, setFieldValue, setFieldError, isSubmitting, values, resetForm }) => {
          return (
            <div>
              <Card sx={{ width: '100%', padding: 2 }}>
                <Form>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      {!isAddingNewUser ? (
                        <TextField
                          label={
                            <>
                              {'Assignment UserID'} <RedAsterisk>*</RedAsterisk>
                            </>
                          }
                          name="AssignmentUserID"
                          fullWidth
                          error={touched.AssignmentUserID && Boolean(errors.AssignmentUserID)}
                          helperText={touched.AssignmentUserID && errors.AssignmentUserID}
                          value={values.AssignmentUserID}
                          onClick={handleUserClick}
                          InputProps={{
                            readOnly: true,
                            endAdornment: (
                              <>
                                {values.AssignmentUserID && (
                                  <InputAdornment position="end" sx={{ mr: 3 }}>
                                    <IconButton
                                      onClick={() => clearUserSelection(setFieldValue)}
                                    >
                                      <CloseIcon />
                                    </IconButton>
                                  </InputAdornment>
                                )}
                                {
                                  anchorElUser ?
                                    <InputAdornment position="end">
                                      <ExpandLess />
                                    </InputAdornment> :
                                    <InputAdornment position="end">
                                      <ExpandMoreIcon />
                                    </InputAdornment>
                                }
                              </>
                            ),
                          }}
                          onBlur={() => {
                            if (!values.AssignmentUserID) {
                              setFieldError('AssignmentUserID', 'User  is required');
                            }
                          }}
                        >
                          <MenuItem value="">Select a user</MenuItem>
                          {users.map(user => (
                            <MenuItem key={user.UserID} value={user.UserID}>
                              {`${user.UserID || ''} - ${user.UserEmail || ''}`}
                            </MenuItem>
                          ))}
                          <MenuItem
                            onClick={() => {
                              setIsAddingNewUser(true);
                              setFieldValue('AssignmentUserID', '');
                            }}
                          >
                            + Add New User
                          </MenuItem>
                        </TextField>
                      ) : (
                        <TextField
                          label="Enter new user email"
                          name="AssignmentUserID"
                          fullWidth
                          error={touched.AssignmentUserID && Boolean(errors.AssignmentUserID) || userExistsError || emailValidationError}
                          helperText={(touched.AssignmentUserID && errors.AssignmentUserID) || userExistsError || emailValidationError}
                          value={values.AssignmentUserID}
                          onChange={(e) => {
                            const email = e.target.value;
                            setFieldValue('AssignmentUserID', email);
                            const isCompleteEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
                            isCompleteEmail && debouncedCheckUserExists(email, setFieldValue);
                          }}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => {
                                    setIsAddingNewUser(false);
                                    setFieldValue('AssignmentUserID', '');
                                    setUserExistsError(null)
                                    setExistingUserID(null);
                                    setEmailValidationError(null);
                                    resetForm();
                                  }}
                                >
                                  <CloseIcon />
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        label={
                          <>
                            {'Assignment CredentialID'} <RedAsterisk>*</RedAsterisk>
                          </>
                        }
                        name="AssignmentCredentialID"
                        fullWidth
                        error={touched.AssignmentCredentialID && Boolean(errors.AssignmentCredentialID)}
                        helperText={touched.AssignmentCredentialID && errors.AssignmentCredentialID}
                        value={values.AssignmentCredentialID.join(', ')}
                        onClick={handleCredentialClick}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <>
                              {values.AssignmentCredentialID.length > 0 && (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={() => clearCredentialSelection(setFieldValue)}
                                  >
                                    <CloseIcon />
                                  </IconButton>
                                </InputAdornment>
                              )}
                              {
                                anchorElCredential ?
                                  <InputAdornment position="end">
                                    <ExpandLess />
                                  </InputAdornment> :
                                  <InputAdornment position="end">
                                    <ExpandMoreIcon />
                                  </InputAdornment>
                              }
                            </>
                          ),
                        }}
                        onBlur={() => {
                          if (values.AssignmentCredentialID.length === 0) {
                            setFieldError(
                              'AssignmentCredentialID',
                              'At least one credential is required'
                            );
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={isSubmitting || userExistsError || emailValidationError}
                        startIcon={isSubmitting && <CircularProgress size={24} color="inherit" />}
                      >
                        {isSubmitting ? 'Creating Assignment...' : 'Create Assignment'}
                      </Button>
                    </Grid>
                  </Grid>
                </Form>
              </Card>
              <CustomMenu
                anchorEl={anchorElUser}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUser}
              >
                <TextField
                  label="Search users"
                  fullWidth
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {searchQuery && (
                          <IconButton
                            onClick={() => {
                              setSearchQuery('');
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                  InputLabelProps={{
                    style: { marginLeft: '15px', marginTop: '7px' },
                  }}
                  sx={{ padding: '8px 16px' }}
                />
                <MenuItem
                  onClick={() => {
                    setIsAddingNewUser(true);
                    setFieldValue('AssignmentUserID', '');
                    handleCloseUser();
                  }}
                  sx={{
                    color: '#42A00E',
                    fontWeight: 'bold',
                    backgroundColor: '#f0f4c3',
                    '&:hover': {
                      backgroundColor: '#dcedc8',
                      boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                    },
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 16px',
                    transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
                  }}
                >
                  <span>+ Add New User</span>
                  <AddCircleIcon sx={{ color: '#42A00E' }} />
                </MenuItem>
                {/* {filteredUsers.map((user) => (
                  <MenuItem
                    key={user.UserID}
                    onClick={() => {
                      setFieldValue('AssignmentUserID', user.UserID);
                      handleCloseUser();
                    }}
                  >
                    {`${user?.UserID || ''} - ${user.UserEmail || ''}`}
                  </MenuItem>
                ))} */}
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => (
                    <MenuItem
                      key={user.UserID}
                      onClick={() => {
                        setFieldValue('AssignmentUserID', user.UserID);
                        handleCloseUser();
                      }}
                    >
                      {`${user?.UserID || ''} - ${user.UserEmail || ''}`}
                    </MenuItem>
                  ))
                ) : (
                  searchQuery !== '' && (
                    <Typography variant="body1" color="error" sx={{ marginTop: '8px', textAlign: 'center' }}>
                      No user found
                    </Typography>
                  )
                )}
                {/* {filteredUsers.length == 0 &&
                <Typography variant="body1" color="error" sx={{ marginTop: '8px', textAlign: 'center', padding: '8px 16px' }}>
                  No users found
                </Typography>
                } */}
              </CustomMenu>

              <CustomMenu
                anchorEl={anchorElCredential}
                open={Boolean(anchorElCredential)}
                onClose={handleCloseCredential}
              >
                {credentials.map((credential) => (
                  <MenuItem key={credential.CredentialID}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.AssignmentCredentialID.includes(credential.CredentialID)}
                          onChange={(e) => {
                            const { checked } = e.target;
                            setFieldValue(
                              'AssignmentCredentialID',
                              checked
                                ? [...values.AssignmentCredentialID, credential.CredentialID]
                                : values.AssignmentCredentialID.filter(id => id !== credential.CredentialID)
                            );
                          }}
                        />
                      }
                      label={credential.CredentialName}
                    />
                  </MenuItem>
                ))}
              </CustomMenu>
            </div>
          );
        }}
      </Formik>
    </>
  );
}


export default AssignmentForm;
